import React, { useMemo } from 'react';
import { AbsoluteFill, interpolate } from 'remotion';
import type { TransitionPresentation, TransitionPresentationComponentProps } from '@remotion/transitions';

export type BlackoutAndLightLeakProps = {
  enterStyle?: React.CSSProperties;
  exitStyle?: React.CSSProperties;
  intensity?: number;
};

const FadeBlackTransition: React.FC<
  TransitionPresentationComponentProps<BlackoutAndLightLeakProps>
> = ({ children, presentationDirection, presentationProgress, passedProps }) => {
  const isEntering = presentationDirection === 'entering';

  // For entering: fade in from black (opacity 0 → 1)
  // For exiting: fade out to black (opacity 1 → 0)
  const opacity = isEntering
    ? interpolate(presentationProgress, [0, 1], [0, 1])
    : interpolate(presentationProgress, [0, 1], [1, 0]);

  const style: React.CSSProperties = useMemo(() => ({
    width: '100%',
    height: '100%',
    backgroundColor: 'black', // Always black background
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    ...(isEntering ? passedProps.enterStyle : passedProps.exitStyle),
  }), [isEntering, passedProps.enterStyle, passedProps.exitStyle]);

  const childStyle: React.CSSProperties = useMemo(() => ({
    width: '100%',
    height: '100%',
    opacity,
  }), [opacity]);

  return (
    <AbsoluteFill style={style}>
      <div style={childStyle}>{children}</div>
    </AbsoluteFill>
  );
};

const LightLeakTransition: React.FC<TransitionPresentationComponentProps<BlackoutAndLightLeakProps>> = ({
  children,
  presentationDirection,
  presentationProgress,
  passedProps,
}) => {
  const isEntering = presentationDirection === 'entering';
  const intensity = passedProps.intensity ?? 1;

  // Animate the sweep for both entering and exiting
  const progress = isEntering ? presentationProgress : 1 - presentationProgress;
  // Animate opacity for a soft sweep
  const opacity = interpolate(progress, [0, 0.5, 1], [0, intensity, 0]);

  // The leak is a large, rotated, blurred ellipse
  const leakStyle: React.CSSProperties = useMemo(() => ({
    position: 'absolute',
    width: '200%',
    height: '120%',
    left: '-50%',
    top: '-10%',
    opacity,
    pointerEvents: 'none',
    background: 'radial-gradient(ellipse at 20% 80%, rgba(255,200,100,0.7) 0%, rgba(255,255,255,0.5) 40%, rgba(255,150,50,0.3) 80%, rgba(255,255,255,0) 100%)',
    filter: 'blur(40px)',
    transform: `rotate(-25deg) translate(${progress * 60 - 30}%, ${-progress * 60 + 30}%)`,
    mixBlendMode: 'screen',
    zIndex: 2,
    ...(isEntering ? passedProps.enterStyle : passedProps.exitStyle),
  }), [opacity, progress, isEntering, passedProps.enterStyle, passedProps.exitStyle]);

  return (
    <AbsoluteFill>
      {children}
      <div style={leakStyle} />
    </AbsoluteFill>
  );
};

export const fadeBlack = (props?: BlackoutAndLightLeakProps): TransitionPresentation<BlackoutAndLightLeakProps> => {
  return {
    component: FadeBlackTransition,
    props: props ?? {},
  };
};

export const lightLeak = (props?: BlackoutAndLightLeakProps): TransitionPresentation<BlackoutAndLightLeakProps> => {
  return {
    component: LightLeakTransition,
    props: props ?? {},
  };
}; 